import { Component, Inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { PostAssetUploadService } from 'src/app/services/post-asset-upload.service';
import {
  ShootAssetUploadService,
  UploadProgress,
} from 'src/app/services/shoot-asset-upload.service';
import {
  UploadMode,
  UploadAssetType,
  getAllowedExtensions,
  formatAllowedHint,
  validateFileExtension,
} from './upload-extensions';

export { UploadMode, UploadAssetType };

export interface AssetUploadDialogData {
  mode: UploadMode;
  assetType?: UploadAssetType;
  shootAssetId?: string;
  productTouchAssetId?: string;
  file?: File;
}

@Component({
  selector: 'app-asset-upload-dialog',
  templateUrl: './asset-upload-dialog.component.html',
  styleUrls: ['./asset-upload-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class AssetUploadDialogComponent implements OnInit, OnDestroy {
  file: File | null = null;
  isDragOver = false;
  progress: UploadProgress | null = null;
  statusText = '';
  validationError = '';

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: AssetUploadDialogData,
    private dialogRef: MatDialogRef<AssetUploadDialogComponent>,
    private postUploadService: PostAssetUploadService,
    private shootUploadService: ShootAssetUploadService,
  ) {}

  get title(): string {
    return this.data.mode === 'shoot' ? 'Upload Shoot Asset' : 'Upload Post Asset';
  }

  get acceptAttr(): string {
    return this.allowedExtensions.join(',');
  }

  get hintText(): string {
    return formatAllowedHint(this.allowedExtensions);
  }

  private get allowedExtensions(): string[] {
    return getAllowedExtensions(this.data.mode, this.data.assetType);
  }

  ngOnInit(): void {
    if (this.data.file) {
      this.selectFile(this.data.file);
    }

    const progress$ =
      this.data.mode === 'shoot' ? this.shootUploadService.progress$ : this.postUploadService.progress$;

    progress$.pipe(takeUntil(this.destroy$)).subscribe((progress) => {
      this.progress = progress;
      this.statusText = this.deriveStatusText(progress);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.activeService().resetIfIdle();
  }

  // Drag and drop handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length === 1) {
      this.selectFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length === 1) {
      this.selectFile(input.files[0]);
    }
  }

  private selectFile(file: File): void {
    this.validationError = '';
    const result = validateFileExtension(file, this.allowedExtensions);
    if (!result.valid) {
      this.validationError = result.error;
      return;
    }
    this.file = file;
  }

  startUpload(): void {
    if (!this.file) return;
    if (this.data.mode === 'shoot') {
      if (!this.data.productTouchAssetId) return;
      this.shootUploadService.startUpload(this.file, this.data.productTouchAssetId);
    } else {
      if (!this.data.shootAssetId) return;
      this.postUploadService.startUpload(this.file, this.data.shootAssetId);
    }
  }

  onPause(): void {
    this.activeService().pauseUpload();
  }

  onResume(): void {
    this.activeService().startResume();
  }

  onCancel(): void {
    this.activeService()
      .cancelUpload()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialogRef.close(false);
      });
  }

  onClose(): void {
    this.dialogRef.close(true);
  }

  onRemoveFile(): void {
    this.file = null;
    this.validationError = '';
  }

  private activeService(): PostAssetUploadService | ShootAssetUploadService {
    return this.data.mode === 'shoot' ? this.shootUploadService : this.postUploadService;
  }

  get canStartUpload(): boolean {
    return !!this.file && !this.progress;
  }

  get canPause(): boolean {
    return (
      !!this.progress &&
      (this.progress.state === 'uploading' || this.progress.state === 'hashing')
    );
  }

  get canResume(): boolean {
    return !!this.progress && this.progress.state === 'paused';
  }

  get canCancel(): boolean {
    return (
      !this.progress ||
      this.progress.state === 'uploading' ||
      this.progress.state === 'hashing' ||
      this.progress.state === 'paused'
    );
  }

  get isDone(): boolean {
    return !!this.progress && this.progress.state === 'done';
  }

  get isError(): boolean {
    return !!this.progress && this.progress.state === 'error';
  }

  get progressMode(): 'determinate' | 'indeterminate' {
    if (!this.progress) return 'determinate';
    if (this.progress.state === 'hashing' || this.progress.state === 'completing') {
      return 'indeterminate';
    }
    return 'determinate';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, i);
    return `${value.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  }

  private deriveStatusText(progress: UploadProgress | null): string {
    if (!progress) return '';
    switch (progress.state) {
      case 'hashing':
        return 'Reading file...';
      case 'uploading':
        return `Uploading (${progress.percentage}%)...`;
      case 'completing':
        return 'Completing...';
      case 'done':
        return 'Done!';
      case 'paused':
        return progress.error
          ? `Paused after error at ${progress.percentage}% — ${progress.error}`
          : `Paused at ${progress.percentage}%`;
      case 'error':
        return `Error: ${progress.error || 'Upload failed'}`;
      default:
        return '';
    }
  }
}
